/**
 * Form per avviare un ricalcolo del listino prezzi.
 *
 * Mostra il breakdown dei costi usati nel ricalcolo margini, il totale mensile
 * e permette di avviare il ricalcolo per un mese/anno/unità specifici.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Calculator } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

/* ─────────────────────────── Utility ─────────────────────────── */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/* ─────────────────────────── Interfacce ─────────────────────────── */

interface Unita {
  id: string;
  nome: string;
}

interface CostoFisso {
  id: string;
  descrizione: string;
  importo: string;
  periodicita: string;
  importo_mensile: string;
  attivo: boolean;
}

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/* ─────────────────────────── Componente ─────────────────────────── */

export const RicalcoloForm: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useFormatters();
  const now = new Date();

  const [unita, setUnita] = useState<Unita[]>([]);
  const [unitaId, setUnitaId] = useState('');
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [costi, setCosti] = useState<CostoFisso[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [risultato, setRisultato] = useState<{
    ricalcolo_id: string;
    totale_costi_fissi_mensili: string;
    stato: string;
  } | null>(null);

  /* Carica unità */
  useEffect(() => {
    fetch('/api/v1/unita', { headers: authHeadersGet() })
      .then((r) => r.json())
      .then((data: Unita[]) => setUnita(data))
      .catch(() => {});
  }, []);

  /* Carica costi fissi attivi */
  const caricaCosti = useCallback(async () => {
    if (!unitaId) return;
    setLoading(true);
    const res = await fetch(
      `/api/v1/prezzi/costi-fissi?unita_organizzativa_id=${unitaId}`,
      { headers: authHeadersGet() },
    );
    if (res.ok) {
      const data: CostoFisso[] = await res.json();
      setCosti(data.filter((c) => c.attivo));
    }
    setLoading(false);
  }, [unitaId]);

  useEffect(() => {
    caricaCosti();
  }, [caricaCosti]);

  const totaleMensile = costi.reduce(
    (acc, c) => acc + Number(c.importo_mensile),
    0,
  );

  /* Avvia ricalcolo */
  const avviaRicalcolo = async () => {
    setSubmitting(true);
    const res = await fetch('/api/v1/prezzi/listino/ricalcola', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        unita_organizzativa_id: unitaId,
        anno,
        mese,
      }),
    });
    if (res.ok) {
      setRisultato(await res.json());
    }
    setSubmitting(false);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-outfit font-semibold text-foreground">
            Nuovo ricalcolo listino
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Qui gestisci i costi usati per il ricalcolo margini, non una contabilita' separata.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(unitaId ? `/prezzi/costi-fissi?uo=${unitaId}` : '/prezzi/costi-fissi')}
        >
          <Calculator className="w-4 h-4" />
          Gestisci costi per ricalcolo
        </Button>
      </div>

      {/* Selettori */}
      <div className="flex gap-3 flex-wrap">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Unità organizzativa
          </label>
          <select
            value={unitaId}
            onChange={(e) => { setUnitaId(e.target.value); setRisultato(null); }}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm w-64"
          >
            <option value="">Seleziona unità…</option>
            {unita.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Anno
          </label>
          <select
            value={anno}
            onChange={(e) => setAnno(Number(e.target.value))}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Mese
          </label>
          <select
            value={mese}
            onChange={(e) => setMese(Number(e.target.value))}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
          >
            {MESI.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {unitaId && (
        <>
          {/* Breakdown costi fissi */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Costi usati nel ricalcolo</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center text-muted-foreground py-4">
                  Caricamento…
                </div>
              ) : costi.length === 0 ? (
                <div className="text-center text-muted-foreground py-4">
                  Nessun costo configurato per il ricalcolo di questa unita'
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-medium">Descrizione</TableHead>
                      <TableHead className="font-medium text-right">Importo</TableHead>
                      <TableHead className="font-medium">Periodicità</TableHead>
                      <TableHead className="font-medium text-right">Mensile</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costi.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.descrizione}</TableCell>
                        <TableCell className="text-right">{fmt.currency(c.importo)}</TableCell>
                        <TableCell>{c.periodicita}</TableCell>
                        <TableCell className="text-right">{fmt.currency(c.importo_mensile)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-semibold">
                      <TableCell colSpan={3} className="text-right">
                        Totale costi fissi mensili
                      </TableCell>
                      <TableCell className="text-right">
                        {fmt.currency(totaleMensile)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Risultato o bottone */}
          {risultato ? (
            <Card>
              <CardContent className="p-6 space-y-2">
                <p className="text-green-700 dark:text-green-400 font-medium">
                  Ricalcolo avviato con successo
                </p>
                <p className="text-sm text-muted-foreground">
                  Costi fissi mensili: {fmt.currency(risultato.totale_costi_fissi_mensili)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Stato: {risultato.stato}
                </p>
                <div className="pt-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/prezzi/listino')}
                  >
                    Vai al listino
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Button
              onClick={avviaRicalcolo}
              disabled={submitting || costi.length === 0}
            >
              <Calculator className="w-4 h-4 mr-1" />
              {submitting ? 'Ricalcolo in corso…' : 'Proponi al titolare'}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
